import { jsx as _jsx } from "react/jsx-runtime";
import { SEARCH_FIELD_PROPS } from '@/components/shared/touchInput';
import { Input } from '@/components/ui/input';
import { formatTagSearchInput, parseTagSearchInput } from '@/lib/tagSearch';
import { useEffect, useRef, useState } from 'react';
/**
 * Report-search input with `tag:` prefix support, shared by the Studies
 * worklist header and the TC toolbar.
 *
 * The raw text is local state (parsing per keystroke and re-formatting the
 * canonical form back into a controlled input would fight the user mid-token,
 * e.g. while an opening quote is still unclosed). `keyword`/`tag` are the
 * external truth: edits parse the raw text and call `onApply` (optionally
 * debounced); external changes (Clear buttons, URL navigation) re-seed the
 * raw text whenever they diverge from what this input last applied.
 */
export function TagSearchInput({ keyword, tag, onApply, debounceMs = 0, placeholder, title, className, 'aria-label': ariaLabel, 'aria-describedby': ariaDescribedBy, }) {
    const externalValue = formatTagSearchInput({ keyword, tag });
    const [raw, setRaw] = useState(externalValue);
    const lastAppliedRef = useRef(externalValue);
    const debounceRef = useRef(null);
    // External reset (Clear filters, back/forward nav): resync the visible text
    // only when the outside state no longer matches what this input produced.
    useEffect(() => {
        if (externalValue !== lastAppliedRef.current) {
            lastAppliedRef.current = externalValue;
            setRaw(externalValue);
        }
    }, [externalValue]);
    useEffect(() => {
        return () => {
            if (debounceRef.current)
                clearTimeout(debounceRef.current);
        };
    }, []);
    const apply = (value) => {
        const parsed = parseTagSearchInput(value);
        lastAppliedRef.current = formatTagSearchInput(parsed);
        onApply(parsed);
    };
    return (_jsx(Input, { type: "search", ...SEARCH_FIELD_PROPS, value: raw, placeholder: placeholder, title: title, "aria-label": ariaLabel, "aria-describedby": ariaDescribedBy, className: className, onChange: (e) => {
            const value = e.target.value;
            setRaw(value);
            if (debounceRef.current)
                clearTimeout(debounceRef.current);
            if (debounceMs > 0) {
                debounceRef.current = setTimeout(() => apply(value), debounceMs);
            }
            else {
                apply(value);
            }
        } }));
}
